import { useEventContent } from "@/context/EventContentContext";
import SocialLinks from "@/components/SocialLinks";

const FALLBACK_ABOUT_TITLE = "Una experiencia que rinde homenaje al sabor del Bajío.";
const FALLBACK_ABOUT_TEXT =
  "Cuchara Grande es un festival gastronómico que reúne a chefs, productores, aliados educativos e instituciones que han construido la identidad culinaria de León y del Bajío.\n\nCada edición es una celebración del talento local, de la memoria gastronómica y de la creatividad de quienes hacen cocina con respeto al producto, a la técnica y a la cultura.\n\nNo es solo una cena: es un punto de encuentro entre generaciones, escuelas, productores y comensales que comparten una misma mesa por una misma ciudad.";
const FALLBACK_CARD_TEXT =
  "Esta edición celebra la historia, creatividad y riqueza culinaria de León como parte de los festejos por su 450 aniversario. Una mesa monumental que honra la memoria de la ciudad y proyecta su futuro gastronómico.";

const InfoCard = ({ label, value, testid }) => (
  <div className="card-cg p-6" data-testid={testid}>
    <div className="overline text-[var(--brand-muted)]">{label}</div>
    <div className="font-serif-cg text-xl mt-2 leading-snug">{value}</div>
  </div>
);

function toParagraphs(value) {
  return String(value || "")
    .split(/\n\s*\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function About() {
  const { event } = useEventContent();
  const title = event.aboutTitle || FALLBACK_ABOUT_TITLE;
  const paragraphs = toParagraphs(event.aboutText || FALLBACK_ABOUT_TEXT);
  const cardEyebrow = event.aboutCardEyebrow || "Edición especial";
  const cardTitle = event.aboutCardTitle || "León 450";
  const cardText = event.commemorativeText || FALLBACK_CARD_TEXT;
  const aboutImage = event.aboutImage || "";

  return (
    <div data-testid="about-page" className="section-pad">
      <div className="container-cg">
        <div className="overline text-[var(--brand-primary)]">{event.aboutEyebrow || "Sobre el evento"}</div>
        <h1 className="font-serif-cg text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 max-w-3xl">
          {title}
        </h1>

        <div className="grid md:grid-cols-12 gap-10 mt-14">
          <div className="md:col-span-7 space-y-5 text-lg leading-relaxed text-[var(--brand-ink)]/85">
            {paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <aside className="md:col-span-5 space-y-5">
            <div className="card-cg p-8 bg-[var(--brand-surface-alt)] border-[var(--brand-primary)]/20">
              <div className="overline text-[var(--brand-accent)]">{cardEyebrow}</div>
              <h2 className="font-serif-cg text-3xl md:text-4xl mt-2 tracking-tight">{cardTitle}</h2>
              <div className="hairline my-5" />
              <p className="text-[15px] leading-relaxed text-[var(--brand-ink)]/85">{cardText}</p>
            </div>

            {aboutImage ? (
              <div className="card-cg overflow-hidden img-zoom-host">
                <img
                  src={aboutImage}
                  alt={event.aboutImageAlt || "Imagen del evento"}
                  className="img-zoom w-full aspect-[4/3] object-cover"
                />
              </div>
            ) : null}
          </aside>
        </div>

        <div className="mt-20">
          <div className="overline text-[var(--brand-primary)]">Información general</div>
          <h2 className="font-serif-cg text-3xl md:text-4xl tracking-tight mt-3">Datos del evento</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            <InfoCard testid="info-date" label="Fecha" value={event.date} />
            <InfoCard testid="info-time" label="Horario" value={event.time} />
            <InfoCard testid="info-venue" label="Sede" value={event.venue} />
            <InfoCard testid="info-city" label="Ciudad" value={event.city} />
            <InfoCard testid="info-type" label="Tipo de evento" value={event.type} />
            <InfoCard testid="info-dress" label="Código de vestimenta" value={event.dressCode} />
            <InfoCard testid="info-email" label="Contacto oficial" value={event.contactEmail} />
            <InfoCard testid="info-phone" label="Teléfono" value={event.contactPhone} />
            <InfoCard
              testid="info-social"
              label="Redes sociales"
              value={<SocialLinks social={event.social} className="flex flex-col items-start gap-3" />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
